/**
 * Typed client for the @coach/server Hono API.
 *
 * The transport is `hc<AppType>` — `AppType` is imported as a type-only
 * symbol from the server, so the client gets full end-to-end RPC typing
 * without bundling any server code.
 *
 * The public surface (`CoachApiClient`) intentionally wraps the raw RPC
 * proxy so call-sites in apps stay readable and don't leak Hono's `$get`/
 * `$post` ergonomics. Add new endpoints below, in their domain object.
 */
import { hc } from "hono/client";
import type { AppType } from "@coach/server";
import type {
  LicenseClaims,
  PullRequest,
  PullResponse,
  PushRequest,
  PushResponse,
} from "@coach/shared";

export * from "./admin.js";

export interface CoachApiClient {
  license: {
    /**
     * Mint a fresh JWT for the signed-in user.
     * Multi-club coaches can target a specific org via `orgId`; otherwise the
     * server falls back to the session's active org or the earliest membership.
     */
    issue(args?: {
      orgId?: string;
      deviceId?: string;
    }): Promise<
      | { ok: true; token: string; claims: LicenseClaims }
      | { ok: false; reason: string }
    >;
    /** List the org's licenses (dashboard). */
    current(): Promise<unknown>;
    /**
     * Re-mint a JWT given an old (possibly-expired) one. `httpStatus` lets
     * callers tell an explicit denial (401/403) from transient failures
     * (429/5xx). Throws when the body is not JSON.
     */
    refresh(
      token: string,
      opts?: { deviceId?: string },
    ): Promise<
      ({ ok: true; token: string; claims: LicenseClaims } | { ok: false; reason: string }) & {
        httpStatus: number;
      }
    >;
  };
  billing: {
    /** Public price catalog keyed by `lookup_key`. Cached server-side 1h. */
    catalog(): Promise<BillingCatalogResponse>;
    /** Current org's subscription / license / entitlements / hardware. */
    state(): Promise<BillingStateResponse>;
    /**
     * Start the org's one-time Stripe-free 7-day trial. Owner-only.
     * Returns 409 if a trial or active subscription already exists.
     */
    startTrial(): Promise<{ ok: true; trialEndsAt: string }>;
    /** Start a subscription checkout. Returns the Stripe-hosted URL. */
    startSubscriptionCheckout(
      input: BillingSubscriptionCheckoutInput,
    ): Promise<{ url: string | null; id: string }>;
    /**
     * Change an existing subscription. Upgrades apply immediately (prorated
     * charge now); downgrades take effect at the end of the paid period.
     */
    updateSubscription(
      input: BillingSubscriptionUpdateInput,
    ): Promise<BillingSubscriptionUpdateResult>;
    /** Standalone hardware checkout (no subscription change). */
    startHardwareCheckout(
      input: BillingHardwareCheckoutInput,
    ): Promise<{ url: string | null; id: string }>;
    /** Stripe Customer Portal session URL (manage / cancel / invoices). */
    openPortal(opts?: { returnTo?: string }): Promise<{ url: string }>;
    /** Owner-only hardware-order management surface. */
    hardwareOrders: {
      list(): Promise<{ orders: HardwareOrderRecord[] }>;
      patch(
        id: string,
        patch: HardwareOrderPatchInput,
      ): Promise<{ order: HardwareOrderRecord }>;
      damage(
        id: string,
        input: HardwareOrderDamageInput,
      ): Promise<{ order: HardwareOrderRecord; invoiceItemId: string | null }>;
    };
  };
  sync: {
    pull(req: PullRequest): Promise<PullResponse>;
    push(req: PushRequest): Promise<PushResponse>;
  };
  media: {
    presignUpload(input: { mime: string; bytes: number }): Promise<{
      url: string;
      s3Key: string;
      headers: Record<string, string>;
      expiresIn: number;
      publicUrl: string;
    }>;
  };
  /** Escape hatch — the raw, fully-typed hc proxy for endpoints not yet wrapped. */
  raw: ReturnType<typeof hc<AppType>>;
}

export interface CoachApiOptions {
  baseUrl: string;
  /** Forwarded as `Authorization: Bearer <token>` when present. */
  getAuthToken?: () => Promise<string | null> | string | null;
}

// ─── Billing types (shared client ↔ server) ───────────────────────────────

export interface BillingCatalogItem {
  lookupKey: string;
  priceId: string;
  productId: string;
  /** Stripe product name (e.g. "Extra Trainer"). Null if unavailable. */
  productName: string | null;
  /** Stripe product description — what the item includes. */
  productDescription: string | null;
  currency: string;
  unitAmount: number | null;
  recurring: { interval: string; intervalCount: number } | null;
  taxBehavior: string | null;
}

export interface BillingCatalogResponse {
  items: BillingCatalogItem[];
  missing: string[];
}

export type EffectiveLicenseKind = "manual" | "stripe" | "trial" | "none";

/** Mirrors apps/server/src/licensing/dto.ts — keep identical. */
export interface EffectiveLicenseDto {
  kind: EffectiveLicenseKind;
  valid: boolean;
  blockReason:
    | "no_license"
    | "license_inactive"
    | "expired"
    | "subscription_blocked"
    | null;
  licenseId: string | null;
  plan: "trial" | "monthly" | "annual" | null;
  status: "active" | "expired" | "revoked" | "pending" | null;
  seats: number;
  teams: number;
  storageBlocks: number;
  startsAt: string | null;
  expiresAt: string | null;
}

export interface BillingStateResponse {
  subscription: {
    status: string;
    baseInterval: string;
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    trialEnd: string | null;
    seatsExtra: number;
    teamsExtra: number;
    storageExtraBlocks: number;
  } | null;
  license: {
    id: string;
    plan: string;
    status: string;
    seats: number;
    startsAt: string;
    expiresAt: string | null;
    paidUntil: string | null;
  } | null;
  entitlements: string[];
  hardware: Array<{
    id: string;
    sku: string;
    quantity: number;
    mode: string;
    status: string;
    serialNumber: string | null;
    shippedAt: string | null;
    returnedAt: string | null;
    createdAt: string;
  }>;
  /**
   * Org-wide storage budget breakdown. `limitBytes` already accounts for
   * the 1 GB base plus any paid 5 GB blocks.
   */
  storage: {
    usedBytes: number;
    limitBytes: number;
    remainingBytes: number;
    extraBlocks: number;
  };
  /** Effective license (manual override > Stripe > trial). */
  effective: EffectiveLicenseDto;
  /** Effective coach-seat cap; equals `effective.seats`. */
  seatLimit: number;
  /**
   * Seat cap for hardware orders. Equals `1 + subscription.seatsExtra`
   * (hardware stays Stripe-bound; the server enforces the same rule).
   */
  hardwareSeatLimit: number;
  /** Effective team cap; equals `effective.teams`. */
  teamLimit: number;
  /**
   * Default card on file for the org's Stripe customer. Used to warn the
   * coach which card an immediate upgrade will charge. `null` when there's
   * no subscription or no saved payment method.
   */
  paymentMethod: {
    brand: string | null;
    last4: string | null;
  } | null;
  /** Stripe-free trial state. */
  trial: {
    /** True if the org has never used a trial, has no subscription and never had a manual license. */
    eligible: boolean;
    /** True while the 7-day trial window is still open. */
    active: boolean;
    /** ISO timestamp when the trial ends (or ended), or null. */
    endsAt: string | null;
    /** Whether the org has ever used a trial (even if expired). */
    usedBefore: boolean;
  };
  /** Live org usage counts — used to enforce min seat/team selection. */
  usage: {
    teamCount: number;
    memberCount: number;
  };
}

export interface BillingSubscriptionCheckoutInput {
  interval: "month" | "year";
  extraSeats?: number;
  extraTeams?: number;
  /** Number of paid 5 GB cloud-storage add-on blocks. */
  extraStorageBlocks?: number;
  hardware?: {
    sku:
      | "tablet_basic_buy"
      | "tablet_basic_lease"
      | "tablet_pro_buy"
      | "tablet_pro_lease";
    quantity?: number;
    shippingAddress?: Record<string, unknown>;
    liabilityAccepted?: boolean;
  };
  returnTo?: string;
}

export interface BillingHardwareCheckoutInput {
  sku:
    | "tablet_basic_buy"
    | "tablet_basic_lease"
    | "tablet_pro_buy"
    | "tablet_pro_lease";
  quantity?: number;
  shippingAddress: Record<string, unknown>;
  liabilityAccepted: boolean;
  returnTo?: string;
}

/** Change an existing subscription's interval and/or add-on quantities. */
export interface BillingSubscriptionUpdateInput {
  interval?: "month" | "year";
  extraSeats: number;
  extraTeams: number;
  extraStorageBlocks: number;
}

/** Result of a subscription change (upgrade now / downgrade at renewal). */
export interface BillingSubscriptionUpdateResult {
  direction: "upgrade" | "downgrade" | "none";
  /** ISO timestamp the change takes effect. `null` when nothing changed. */
  effectiveAt: string | null;
  /** For upgrades: amount charged immediately (smallest currency unit). */
  immediateChargeCents: number | null;
  currency: string | null;
}

export type HardwareOrderStatus =
  | "pending"
  | "shipped"
  | "active"
  | "return_requested"
  | "returned"
  | "damaged";

/**
 * Full HardwareOrder row as returned by the owner-only admin listing.
 * Mirrors `packages/db/prisma/schema.prisma#HardwareOrder` with timestamps
 * serialised as ISO strings (JSON wire format).
 */
export interface HardwareOrderRecord {
  id: string;
  orgId: string;
  sku: string;
  quantity: number;
  mode: string;
  status: HardwareOrderStatus;
  serialNumber: string | null;
  trackingNumber: string | null;
  trackingUrl: string | null;
  damageNote: string | null;
  shippedAt: string | null;
  returnedAt: string | null;
  liabilityAccepted: boolean;
  liabilityAcceptedAt: string | null;
  shippingAddress: Record<string, unknown>;
  stripeCheckoutSessionId: string | null;
  stripeInvoiceId: string | null;
  stripeSubscriptionItemId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface HardwareOrderPatchInput {
  status?: HardwareOrderStatus;
  serialNumber?: string | null;
  trackingNumber?: string | null;
  trackingUrl?: string | null;
  shippedAt?: string | null;
  returnedAt?: string | null;
}

export interface HardwareOrderDamageInput {
  /** Damage fee in the smallest currency unit (e.g. 19900 = €199.00). */
  amountCents: number;
  reason: string;
  /** ISO 4217 currency code; defaults to `eur` server-side. */
  currency?: string;
}

export function createCoachApi(opts: CoachApiOptions): CoachApiClient {
  const rpc = hc<AppType>(opts.baseUrl, {
    async fetch(input: RequestInfo | URL, init?: RequestInit) {
      const token =
        typeof opts.getAuthToken === "function"
          ? await opts.getAuthToken()
          : null;
      const headers = new Headers(init?.headers);
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return fetch(input, { ...init, headers, credentials: "include" });
    },
  });

  return {
    license: {
      async issue(args) {
        const r = await rpc.api.v1.license.issue.$post({
          json: {
            ...(args?.orgId ? { orgId: args.orgId } : {}),
            ...(args?.deviceId ? { deviceId: args.deviceId } : {}),
          },
        });
        if (!r.ok) {
          // Map HTTP failure (401, 403, 5xx) into the discriminated-union
          // shape callers expect — otherwise `issued.reason` is undefined
          // downstream and consumers crash trying to format it.
          if (r.status === 401) return { ok: false, reason: "unauthorized" };
          // 402 / 403 / 409 carry a meaningful `{ reason }` body (e.g.
          // `trial_exhausted`, `license-expired`, `subscription-blocked`,
          // `not_a_member`, `no_organization`). Preserve it so the UI can
          // show the right gating surface (paywall vs. onboarding) instead
          // of a generic "forbidden".
          if (r.status === 402 || r.status === 403 || r.status === 409) {
            const body = (await r.json().catch(() => null)) as {
              reason?: string;
            } | null;
            return { ok: false, reason: body?.reason ?? `http_${r.status}` };
          }
          return { ok: false, reason: `http_${r.status}` };
        }
        return (await r.json()) as
          | { ok: true; token: string; claims: LicenseClaims }
          | { ok: false; reason: string };
      },
      async current() {
        const r = await rpc.api.v1.license.current.$get();
        if (!r.ok) throw new Error(`license.current failed: ${r.status}`);
        return r.json();
      },
      async refresh(token, refreshOpts) {
        const r = await rpc.api.v1.license.refresh.$post({
          json: {
            token,
            ...(refreshOpts?.deviceId
              ? { deviceId: refreshOpts.deviceId }
              : {}),
          },
        });
        const body = (await r.json()) as
          | { ok: true; token: string; claims: LicenseClaims }
          | { ok: false; reason: string };
        return Object.assign(body, { httpStatus: r.status });
      },
    },
    sync: {
      async pull(req) {
        const r = await rpc.api.v1.sync.pull.$post({ json: req });
        return (await r.json()) as PullResponse;
      },
      async push(req) {
        const r = await rpc.api.v1.sync.push.$post({ json: req });
        return (await r.json()) as PushResponse;
      },
    },
    billing: {
      async catalog() {
        const r = await rpc.api.v1.billing.catalog.$get();
        if (!r.ok) throw new Error(`billing.catalog failed: ${r.status}`);
        return (await r.json()) as BillingCatalogResponse;
      },
      async state() {
        const r = await rpc.api.v1.billing.state.$get();
        if (!r.ok) throw new Error(`billing.state failed: ${r.status}`);
        return (await r.json()) as BillingStateResponse;
      },
      async startTrial() {
        const r = await fetch(
          `${opts.baseUrl.replace(/\/$/, "")}/api/v1/billing/trial`,
          {
            method: "POST",
            credentials: "include",
            headers: { "content-type": "application/json" },
          },
        );
        if (!r.ok) {
          const body = await r.text().catch(() => "");
          throw new Error(`billing.startTrial failed: ${r.status} ${body}`);
        }
        return (await r.json()) as { ok: true; trialEndsAt: string };
      },
      async startSubscriptionCheckout(input) {
        const r = await rpc.api.v1.billing.checkout.subscription.$post({
          json: input,
        });
        if (!r.ok) {
          const body = await r.text().catch(() => "");
          throw new Error(
            `billing.checkout.subscription failed: ${r.status} ${body}`,
          );
        }
        return (await r.json()) as { url: string | null; id: string };
      },
      async updateSubscription(input) {
        const r = await rpc.api.v1.billing.subscription.$post({ json: input });
        if (!r.ok) {
          const body = await r.text().catch(() => "");
          throw new Error(
            `billing.updateSubscription failed: ${r.status} ${body}`,
          );
        }
        return (await r.json()) as BillingSubscriptionUpdateResult;
      },
      async startHardwareCheckout(input) {
        const r = await rpc.api.v1.billing.checkout.hardware.$post({
          json: input,
        });
        if (!r.ok) {
          const body = await r.text().catch(() => "");
          throw new Error(
            `billing.checkout.hardware failed: ${r.status} ${body}`,
          );
        }
        return (await r.json()) as { url: string | null; id: string };
      },
      async openPortal(portalOpts) {
        const r = await rpc.api.v1.billing.portal.$post({
          json: portalOpts?.returnTo ? { returnTo: portalOpts.returnTo } : {},
        });
        if (!r.ok) {
          const body = await r.text().catch(() => "");
          throw new Error(`billing.portal failed: ${r.status} ${body}`);
        }
        return (await r.json()) as { url: string };
      },
      hardwareOrders: {
        async list() {
          const r = await rpc.api.v1.billing.hardware.orders.$get();
          if (!r.ok) {
            const body = await r.text().catch(() => "");
            throw new Error(
              `billing.hardwareOrders.list failed: ${r.status} ${body}`,
            );
          }
          return (await r.json()) as { orders: HardwareOrderRecord[] };
        },
        async patch(id, patch) {
          // Param/body shape: hc routes `:id` through `param.id`. We cast
          // through `unknown` because Hono's inferred type for the path
          // template is wider than the Record<string,string> hc expects.
          const r = await rpc.api.v1.billing.hardware.orders[":id"].$patch({
            param: { id },
            json: patch,
          } as unknown as Parameters<
            (typeof rpc.api.v1.billing.hardware.orders)[":id"]["$patch"]
          >[0]);
          if (!r.ok) {
            const body = await r.text().catch(() => "");
            throw new Error(
              `billing.hardwareOrders.patch failed: ${r.status} ${body}`,
            );
          }
          return (await r.json()) as { order: HardwareOrderRecord };
        },
        async damage(id, input) {
          const r = await rpc.api.v1.billing.hardware.orders[
            ":id"
          ].damage.$post({
            param: { id },
            json: input,
          } as unknown as Parameters<
            (typeof rpc.api.v1.billing.hardware.orders)[":id"]["damage"]["$post"]
          >[0]);
          if (!r.ok) {
            const body = await r.text().catch(() => "");
            throw new Error(
              `billing.hardwareOrders.damage failed: ${r.status} ${body}`,
            );
          }
          return (await r.json()) as {
            order: HardwareOrderRecord;
            invoiceItemId: string | null;
          };
        },
      },
    },
    media: {
      async presignUpload(input) {
        const r = await rpc.api.v1.media["upload-url"].$post({ json: input });
        if (!r.ok) {
          const body = await r.text().catch(() => "");
          throw new Error(`media.presignUpload failed: ${r.status} ${body}`);
        }
        return (await r.json()) as {
          url: string;
          s3Key: string;
          headers: Record<string, string>;
          expiresIn: number;
          publicUrl: string;
        };
      },
    },
    raw: rpc,
  };
}
